import { WorldRenderer } from "../WorldRenderer";
import { BaseService } from "./BaseService";
import * as THREE from "three";
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { PlanetService } from "./PlanetService";

interface CubemapSkybox {
  name: string;
  path: string;
  images: string[];
  type: 'cubemap';
}

interface HDRSkybox {
  name: string;
  path: string;
  image: string;
  type: 'hdr';
}

interface ColorSkybox {
  name: string;
  color: THREE.ColorRepresentation;
  type: 'color';
}

type SkyboxConfig = CubemapSkybox | HDRSkybox | ColorSkybox;

const skyboxList: SkyboxConfig[] = [
  {
    name: "Light Blue Sky",
    color: 0xd6f8ff,
    type: 'color'
  },
  {
    name: "Custom Space",
    path: "/assets/skyboxes/space/",
    images: ["right.png", "left.png", "top.png", "bottom.png", "front.png", "back.png"],
    type: 'cubemap'
  }
];

type SkyboxAsset = THREE.CubeTexture | THREE.Texture | THREE.Color;

export class Skybox extends BaseService {
  private skyboxAssets: Map<string, SkyboxAsset> = new Map();
  private loadingPromises: Map<string, Promise<SkyboxAsset>> = new Map();
  private tiler!: PlanetService;

  constructor(worldRenderer: WorldRenderer) {
    super(worldRenderer);
    this.initializeDefaultSkybox();
    this.preloadSkyboxes();
  }

  render(delta: number): void {
    if (!this.tiler) return;

    const targetSkyboxName = this.tiler.cameraInSpace ? "Custom Space" : "Light Blue Sky";
    const targetAsset = this.skyboxAssets.get(targetSkyboxName);

    if (targetAsset && this.worldRenderer.scene.background !== targetAsset) {
      this.worldRenderer.scene.background = targetAsset;
    }
  }

  public async load(): Promise<void> {
    this.tiler = this.getService(PlanetService);
  }

  private initializeDefaultSkybox(): void {
    // Create and set the default blue sky immediately
    const defaultConfig = skyboxList.find(config => config.type === 'color');
    if (defaultConfig && defaultConfig.type === 'color') {
      const skyColor = new THREE.Color(defaultConfig.color);
      this.skyboxAssets.set(defaultConfig.name, skyColor);
      this.worldRenderer.scene.background = skyColor;
    }
  }

  private async preloadSkyboxes(): Promise<void> {
    const loadPromises = skyboxList.map(config => this.loadSkybox(config));

    // Wait for all to complete, but don't let failures block the service
    await Promise.allSettled(loadPromises);
  }

  private async loadSkybox(config: SkyboxConfig): Promise<void> {
    // Skip if already loaded or loading
    if (this.skyboxAssets.has(config.name) || this.loadingPromises.has(config.name)) {
      return;
    }

    const loadPromise = this.createSkyboxAsset(config);
    this.loadingPromises.set(config.name, loadPromise);

    try {
      const asset = await loadPromise;
      this.skyboxAssets.set(config.name, asset);
      // console.log(`✓ Skybox loaded: ${config.name}`);
    } catch (error) {
      console.error(`✗ Skybox failed: ${config.name}`, error);
    } finally {
      this.loadingPromises.delete(config.name);
    }
  }

  private async createSkyboxAsset(config: SkyboxConfig): Promise<SkyboxAsset> {
    switch (config.type) {
      case 'color':
        return new THREE.Color(config.color);

      case 'cubemap':
        return new Promise<THREE.CubeTexture>((resolve, reject) => {
          const loader = new THREE.CubeTextureLoader();
          const texture = loader.load(
            config.images.map(image => config.path + image),
            (loadedTexture) => {
              loadedTexture.magFilter = THREE.LinearFilter;
              loadedTexture.minFilter = THREE.LinearMipMapLinearFilter;
              resolve(loadedTexture);
            },
            undefined,
            reject
          );
        });

      case 'hdr':
        return new Promise<THREE.Texture>((resolve, reject) => {
          const loader = new RGBELoader();
          const texture = loader.load(
            config.path + config.image,
            (loadedTexture) => {
              loadedTexture.mapping = THREE.EquirectangularReflectionMapping;
              loadedTexture.rotation = Math.PI;
              resolve(loadedTexture);
            },
            undefined,
            reject
          );
        });

      default:
        // TypeScript exhaustiveness check
        const _exhaustive: never = config;
        throw new Error(`Unsupported skybox type: ${JSON.stringify(_exhaustive)}`);
    }
  }

  public async setSkybox(name: string): Promise<boolean> {
    const asset = this.skyboxAssets.get(name);
    if (asset) {
      this.worldRenderer.scene.background = asset;
      return true;
    }

    // Try to load if not available
    const config = skyboxList.find(c => c.name === name);
    if (config) {
      await this.loadSkybox(config);
      const loadedAsset = this.skyboxAssets.get(name);
      if (loadedAsset) {
        this.worldRenderer.scene.background = loadedAsset;
        return true;
      }
    }

    return false;
  }

  public getAvailableSkyboxes(): string[] {
    return skyboxList.map(config => config.name);
  }

  public addSkyboxConfig(config: SkyboxConfig): void {
    // Runtime skybox addition - useful for modding/dynamic content
    if (!skyboxList.find(existing => existing.name === config.name)) {
      skyboxList.push(config);
    }
  }

  public dispose(): void {
    this.skyboxAssets.forEach(asset => {
      if (asset instanceof THREE.Texture) {
        asset.dispose();
      }
    });
    this.skyboxAssets.clear();
    this.loadingPromises.clear();
    this.worldRenderer.scene.background = null;
  }
}